// ---------------------------------------------------------------------------
// hudsonRoadIndex — Morgan's hand-curated Pleasant Road Index (Hudson Valley,
// Phase 1). ~70 named roads across Westchester/Putnam, each scored 0–3:
//   3 = actively prefer   2 = prefer when reasonable
//   1 = neutral connector  0 = penalize heavily / avoid
// We turn this into a STRONG ranking lever in scoring: a route that runs the
// 3s/2s is rewarded; one that runs the 0s (I-684, Route 9/9A, …) is penalised.
// "The 0s are as important as the 3s" (her build notes).
//
// Matching is by road NAME against the route's turn-by-turn steps (no geocoding
// needed for the score lever), region-gated to the Hudson Valley so a same-named
// road elsewhere can't trigger it. Geometry-precise detour injection comes later
// via the resolver script.
// ---------------------------------------------------------------------------

import rawRoads from "@/content/hudsonRoads.json";
import type { LngLat } from "@/utils/polyline";

interface HudsonRoad {
  town: string;
  county: string;
  road: string;
  segment: string | null;
  score: number | null;
  type: string | null;
  behavior: string | null;
  why: string | null;
  confidence: string | null;
}

const ROADS = rawRoads as HudsonRoad[];

// Hudson Valley Phase-1 region (Westchester + Putnam, generous pad). The score
// lever only fires for routes whose midpoint falls in here.
export const HUDSON_BBOX = { minLng: -74.15, maxLng: -73.35, minLat: 40.9, maxLat: 41.65 };

export function inHudsonRegion(coords: LngLat[]): boolean {
  if (!coords || coords.length === 0) return false;
  const mid = coords[Math.floor(coords.length / 2)];
  return (
    mid[0] >= HUDSON_BBOX.minLng && mid[0] <= HUDSON_BBOX.maxLng &&
    mid[1] >= HUDSON_BBOX.minLat && mid[1] <= HUDSON_BBOX.maxLat
  );
}

/** Numbered designation → canonical key ("i684", "rt9a"), or null. Interstates
 *  and state routes are kept distinct (I-9 ≠ Route 9). */
function numberedKey(name: string): string | null {
  const s = name.toLowerCase();
  let m = s.match(/\b(?:i|interstate)[-\s]?(\d+[a-z]?)\b/);
  if (m) return `i${m[1]}`;
  m = s.match(/\b(?:route|rte|ny|us|county\s+route|cr|rt)[-\s]?(\d+[a-z]?)\b/);
  if (m) return `rt${m[1]}`;
  return null;
}

/** Named road → canonical key (alphanumerics only). "Guard Hill Road" →
 *  "guardhillroad". Returns null for too-short / numeric-only names. */
function namedKey(name: string): string | null {
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return key.length >= 5 ? key : null;
}

// Build the lookup once. An entry like "Bedford Road / Route 117" registers BOTH
// the named part and the numbered part under its score. Score conflicts across
// towns (rare — Hardscrabble Road is 3 in North Salem, 2 in New Castle) resolve
// to the one furthest from neutral (1), so the stronger signal wins.
const SCORE_BY_KEY: Map<string, number> = (() => {
  const map = new Map<string, number>();
  const consider = (key: string | null, score: number) => {
    if (!key) return;
    const prev = map.get(key);
    if (prev === undefined || Math.abs(score - 1) > Math.abs(prev - 1)) map.set(key, score);
  };
  for (const r of ROADS) {
    if (r.score == null || !r.road) continue;
    for (const part of r.road.split("/").map((p) => p.trim())) {
      consider(numberedKey(part), r.score);
      // Only register the NAMED key when the part isn't itself just a number.
      if (!numberedKey(part)) consider(namedKey(part), r.score);
    }
  }
  return map;
})();

/** The Hudson Index score (0–3) for a step's road name, or null if unlisted. */
export function hudsonScoreForRoad(roadName: string | null | undefined): number | null {
  if (!roadName) return null;
  const num = numberedKey(roadName);
  if (num && SCORE_BY_KEY.has(num)) return SCORE_BY_KEY.get(num)!;
  const named = namedKey(roadName);
  if (named && SCORE_BY_KEY.has(named)) return SCORE_BY_KEY.get(named)!;
  return null;
}

export interface HudsonCoverage {
  /** Meters travelled on score-3 ("actively prefer") roads. */
  preferM: number;
  /** Meters on score-2 ("prefer when reasonable") roads. */
  likeM: number;
  /** Meters on score-0 ("penalize heavily / avoid") roads. */
  avoidM: number;
  /** Names hit, for evidence/blurb. */
  preferNames: string[];
  avoidNames: string[];
}

/** Sum how far a route travels on Hudson-indexed roads, bucketed by score.
 *  Region-gated: returns zeros for routes outside the Hudson Valley. */
export function hudsonCoverage(
  steps: Array<{ roadName?: string; distance?: number }> | undefined,
  routeCoords: LngLat[],
): HudsonCoverage {
  const empty: HudsonCoverage = { preferM: 0, likeM: 0, avoidM: 0, preferNames: [], avoidNames: [] };
  if (!steps || steps.length === 0 || !inHudsonRegion(routeCoords)) return empty;
  const out: HudsonCoverage = { preferM: 0, likeM: 0, avoidM: 0, preferNames: [], avoidNames: [] };
  for (const st of steps) {
    const score = hudsonScoreForRoad(st.roadName);
    if (score == null) continue;
    const m = st.distance ?? 0;
    if (score === 3) { out.preferM += m; if (st.roadName && !out.preferNames.includes(st.roadName)) out.preferNames.push(st.roadName); }
    else if (score === 2) { out.likeM += m; if (st.roadName && !out.preferNames.includes(st.roadName)) out.preferNames.push(st.roadName); }
    else if (score === 0) { out.avoidM += m; if (st.roadName && !out.avoidNames.includes(st.roadName)) out.avoidNames.push(st.roadName); }
  }
  return out;
}

/** The pleasant-score adjustment from Hudson coverage. STRONG lever (Morgan):
 *  reward the 3s/2s, penalise the 0s — bounded so it can't fully dominate. */
export function hudsonScoreAdjustment(cov: HudsonCoverage): number {
  const mi = (m: number) => m / 1609.344;
  // 3s pull hardest, 2s about half. Capped at +36.
  const boost = Math.min(36, mi(cov.preferM) * 9 + mi(cov.likeM) * 4.5);
  // 0s ("penalize heavily") — capped at −40 so a highway slog is decisively
  // out-ranked by any reasonable calmer alternative.
  const penalty = Math.min(40, mi(cov.avoidM) * 10);
  return boost - penalty;
}
